import * as React from "react";
import { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { VariableSizeList } from "react-window";
import { useMediaQuery, useTheme } from "@mui/material";
import "./MultiDropdownStyles.css";

const LISTBOX_PADDING = 4;

/* ---------------- Virtualized List ---------------- */

function renderRow(props) {
    const { data, index, style } = props;
    const item = data[index];

    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING,
        width: "100%",
        boxSizing: "border-box",
        padding: "12px 1px",
        lineHeight: "1.2",
        whiteSpace: "normal",
        wordBreak: "break-word",
        fontSize: "15px",
    };

    return React.cloneElement(item, {
        style: inlineStyle,
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);

    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);

    return ref;
}

const StyledUl = React.forwardRef(({ style, ...props }, ref) => (
    <ul
        ref={ref}
        style={{
            ...style,
            width: "100%",
            listStyle: "none",
            overflowY: "auto",
            padding: 0,
            margin: 0,
        }}
        {...props}
    />
));

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;

    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref} style={{ width: "100%" }}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={220}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType={StyledUl}
                    itemSize={() => 52}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

/* ---------------- Component ---------------- */

export const SingleCheckboxDropdown = ({
    id,
    options = [],
    onChangeItems,
    label,
    selectedItemsData,
    name,
    disabled,
    width,
    height = 40,
    fontSize = 16,
    copyPasteDisabled = true,
    ListboxComponentProps = true,
}) => {
    // const [selectedItem, setSelectedItem] = useState(selectedItemsData || null);
    // const [inputValue, setInputValue] = useState("");
    const [openModal, setOpenModal] = useState(false);

    /* sync external value */
    const selectedItem = React.useMemo(() => {
        if (!selectedItemsData) return null;

        return (
            options.find(
                (opt) => opt.value === selectedItemsData.value
            ) || selectedItemsData
        );
    }, [selectedItemsData, options]);

    /* handle selection */
    const handleChange = (event, value) => {
        // setSelectedItem(value);
        onChangeItems(name, value);
    };

    // const handleInputChange = (event, newInputValue, reason) => {
    //     if (reason === "reset") return;
    //     setInputValue(newInputValue);
    // };

    const handleRemove = () => {
        // setSelectedItem(null);
        onChangeItems(name, null);
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();

            const pastedValues = text
                .split(/[\n,]+/)
                .map((v) => v.trim().toLowerCase())
                .filter(Boolean);

            const match = options.find((opt) =>
                pastedValues.includes(opt.title?.toLowerCase())
            );

            if (match) {
                // setSelectedItem(match);
                onChangeItems(name, match);
            }
        } catch (err) {
            console.error("Clipboard error:", err);
        }
    };
    /* ---------------- UI ---------------- */

    return (
        <div>
            <Autocomplete
                ListboxComponent={ListboxComponentProps ? ListboxComponent : undefined}
                multiple={false}
                disabled={disabled}
                id={id}
                options={options}
                value={selectedItem}
                onChange={handleChange}
                isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                }
                getOptionLabel={(option) =>
                    option?.label || option?.title || ""
                }
                filterOptions={(options, { inputValue }) =>
                    options.filter(opt =>
                        (opt.label || opt.title || "")
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                    )
                }
                renderOption={(props, option) => {
                    const { key, ...rest } = props;

                    return (
                        <li key={key} {...rest}>
                            {option.label || option.title}
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder="Search"
                        disabled={disabled}
                    />
                )}
            />

            {/* clipboard */}
            {copyPasteDisabled && (
                <DialogActions>
                    <Button onClick={handlePasteFromClipboard}>
                        Paste from Clipboard
                    </Button>
                </DialogActions>
            )}

            {/* selected preview modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                <DialogTitle>Selected Item</DialogTitle>
                <DialogContent>
                    {selectedItem ? (
                        <Chip
                            label={selectedItem.label || selectedItem.title}
                            onDelete={handleRemove}
                        />
                    ) : (
                        "No selection"
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};